"use client";

import dynamic from "next/dynamic";
import { cn } from "@/lib/utils";

const Facehash = dynamic(
  () => import("facehash").then((mod) => mod.Facehash),
  {
    ssr: false,
    loading: () => null,
  }
);

const RANKGRIP_COLORS = [
  "#129990",
  "#1EAD9F",
  "#0D736F",
  "#22c4b4",
  "#4ab8b0",
];

/** Stable seed for facehash — prefers display name, then email local-part. */
export function avatarIdentity(
  name?: string | null,
  email?: string | null
): string | null {
  const trimmedName = name?.trim();
  if (trimmedName) return trimmedName;

  const trimmedEmail = email?.trim();
  if (trimmedEmail) {
    const local = trimmedEmail.split("@")[0]?.trim();
    if (local) return local;
    return trimmedEmail;
  }

  return null;
}

type UserAvatarProps = {
  name?: string | null;
  email?: string | null;
  /** @deprecated Ignored — avatars always use facehash. */
  image?: string | null;
  size?: number;
  className?: string;
  showInitial?: boolean;
  interactive?: boolean;
};

/** Trigger facehash "look straight" animation when hovering a parent (e.g. user menu button). */
export function triggerFacehashHover(container: HTMLElement | null, hover: boolean) {
  const facehash = container?.querySelector("[data-facehash]");
  if (!facehash) return;
  facehash.dispatchEvent(
    new MouseEvent(hover ? "mouseenter" : "mouseleave", { bubbles: true })
  );
}

function AvatarPlaceholder({
  size,
  className,
}: {
  size: number;
  className?: string;
}) {
  return (
    <div
      className={cn("shrink-0 bg-muted rounded-md animate-pulse", className)}
      style={{ width: size, height: size }}
      aria-hidden
    />
  );
}

export function UserAvatar({
  name,
  email,
  size = 32,
  className,
  showInitial = true,
  interactive = true,
}: UserAvatarProps) {
  const identity = avatarIdentity(name, email);

  if (!identity) {
    return <AvatarPlaceholder size={size} className={className} />;
  }

  return (
    <Facehash
      name={identity}
      size={size}
      colors={RANKGRIP_COLORS}
      showInitial={showInitial}
      intensity3d="dramatic"
      interactive={interactive}
      className={cn("shrink-0", className)}
    />
  );
}
